import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Button, Stack, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import CardSearchFilters from '../../../Card/CardSearchFilters';
import CardTable from '../../../Card/CardSearchTable';
import BulkReviewerAssignment from './BulkReviewerAssignment';
import { useGetCardOwnersQuery, useSearchCardsQuery } from '../../../../api/cardSlice';
import { useGetHospitalReviewersQuery } from '../../../../api/apiSlice';
import LoadingIndicator from '../../../LoadingIndicator';

function ReviewerAssignmentPage() {
    const { hospitalId } = useParams();
    const [selectedCards, setSelectedCards] = useState(new Set());
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    
    // Default filter values defined once
    const defaultFilters = {
        cardNameSearch: '',
        physicianSearch: [],
        specialty: [],
        reviewerAssignment: 'all',
        specificReviewers: [],
        campaignStatus: 'all',
    };
    
    const [filters, setFilters] = useState(defaultFilters);

    // Helper function to build API filters
    const buildApiFilters = (filters) => {
        const apiFilters = {};
        
        if (filters.physicianSearch.length > 0) apiFilters.physician = filters.physicianSearch;
        if (filters.specialty.length > 0) apiFilters.physician_specialty = filters.specialty;
        
        if (filters.reviewerAssignment === 'specific' && filters.specificReviewers.length > 0) {
            apiFilters.assigned_reviewers = 'specific';
            apiFilters.reviewer_ids = filters.specificReviewers.map(id => parseInt(id));
        } else if (filters.reviewerAssignment === 'none') {
            apiFilters.assigned_reviewers = 'none';
        } else if (filters.reviewerAssignment === 'all') {
            apiFilters.assigned_reviewers = 'all';
        }
        
        if (filters.campaignStatus !== 'all') {
            apiFilters.in_active_campaign = filters.campaignStatus === 'in-campaign';
        }
        
        return apiFilters;
    };

    // Get reviewers for filter options
    const { data: reviewers = [] } = useGetHospitalReviewersQuery(
        hospitalId,
        { skip: !hospitalId }
    );

    // Get physicians for physician and specialty filters
    const { data: physicians = [] } = useGetCardOwnersQuery(
        hospitalId,
        { skip: !hospitalId }
    );

    // Prepare search parameters for the API
    const searchParams = useMemo(() => {
        const params = {
            include: 'reviewers',
            per_page: perPage
        };

        if (filters.cardNameSearch) {
            params.search = filters.cardNameSearch;
        }

        const apiFilters = buildApiFilters(filters);
        if (Object.keys(apiFilters).length > 0) {
            params.filters = apiFilters;
        }

        return params;
    }, [filters, perPage]);

    // Fetch cards with reviewer information
    const { 
        data: cardsResponse, 
        isLoading, 
        isError 
    } = useSearchCardsQuery(
        { 
            hospitalId: hospitalId, 
            searchParams: {
                ...searchParams,
                page
            }
        },
        { 
            skip: !hospitalId 
        }
    );

    // Extract cards and pagination info from response
    const cards = cardsResponse?.data?.data || cardsResponse?.data || [];
    const pagination = cardsResponse?.data?.meta || cardsResponse?.meta || {};

    // Get unique values for filter dropdowns from API data
    const physicianNames = useMemo(() => 
        [...new Set(physicians.map(physician => physician.name).filter(Boolean))].sort(), 
    [physicians]
    );

    const specialties = useMemo(() => 
        [...new Set(physicians.map(physician => physician.specialty).filter(Boolean))].sort(),
    [physicians]
    );

    const reviewerOptions = useMemo(() => 
        reviewers.map(reviewer => ({
            value: reviewer.id,
            label: `${reviewer.first_name} ${reviewer.last_name}`
        })), 
    [reviewers]
    );

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const clearFilters = () => {
        setFilters(defaultFilters);
    };

    const handleClearSelection = () => {
        setSelectedCards(new Set());
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePerPageChange = (event) => {
        setPerPage(event.target.value);
        setPage(1); // Reset to first page when changing per page
    };

    return (
        <Stack p={2} sx={{ height: 'calc(100vh - 65px)' }} direction="row" spacing={2}>
            <Stack spacing={2} sx={{ width: '70%', minHeight: 0 }} direction="column">
                <BulkReviewerAssignment 
                    selectedCards={selectedCards}
                    reviewerOptions={reviewerOptions}
                    clearSelection={handleClearSelection}
                />
                <CardTable 
                    cards={cards}
                    selectedCards={selectedCards}
                    onCardSelect={setSelectedCards}
                />
                
                {/* Loading/Error States */}
                {isLoading && <LoadingIndicator />}
                {isError && (
                    <Typography variant="bold" sx={{ mt: 2, textAlign: 'center', color: 'error.main' }}>
                        Error loading cards. Please try again.
                    </Typography>
                )}
                {(pagination.total === 0 || (pagination.total == null && cards.length === 0)) && !isLoading && !isError && (
                    <Typography variant="bold" sx={{ mt: 2, textAlign: 'center' }}>
                        No cards found matching the current filters.
                    </Typography>
                )}
                {cards.length === 0 && pagination.total > 0 && !isLoading && !isError && (
                    <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                        No cards on this page. Try going back to page {pagination.last_page || 1}.
                    </Typography>
                )}
                
                {/* Pagination Controls */}
                <Stack direction="row" sx={{ justifyContent: 'center', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    <Button 
                        variant="outlined" 
                        size="small" 
                        disabled={page <= 1}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        Previous
                    </Button>
                    <Typography variant="normal" fontSize="14px">
                        Page {page} of {pagination.last_page || 1}
                    </Typography>
                    <Button 
                        variant="outlined" 
                        size="small" 
                        disabled={page >= (pagination.last_page || 1)}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        Next
                    </Button>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                            value={perPage}
                            label="Per Page"
                            onChange={handlePerPageChange}
                        >
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="normal" fontSize="14px" sx={{ color: 'text.secondary' }}>
                        Showing {cards.length} of {pagination.total || cards.length} cards
                    </Typography>
                    <Typography variant="normal" fontSize="14px" sx={{ color: 'text.secondary' }}>
                        {selectedCards.size} selected
                    </Typography>
                </Stack>
            </Stack>

            {/* Right Side Panel - Filters */}
            <Stack spacing={2} sx={{ width: '30%' }}>
                <CardSearchFilters 
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={clearFilters}
                    physicianNames={physicianNames}
                    specialties={specialties}
                    reviewerOptions={reviewerOptions}
                />
            </Stack>
        </Stack>
    );
}

export default ReviewerAssignmentPage;